/**
 * ─────────────────────────────────────────────
 * src/services/changeRequestService.js
 * ─────────────────────────────────────────────
 * The Client Change Request tab on a project.
 *
 *   GET    /projects/:projectId/change-requests             list, newest first
 *   GET    /projects/:projectId/change-requests/:requestId   one request
 *   POST   /projects/:projectId/change-requests              raise one
 *   PUT    /projects/:projectId/change-requests/:requestId    edit one
 *   DELETE /projects/:projectId/change-requests/:requestId    remove one
 *
 * Paths are written WITHOUT the "/api" prefix — the shared axios instance
 * supplies it via baseURL.
 */
import api from "../lib/api";

// ─────────────────────────────────────────────
// SERVER → TABLE ROW
//
// Maps one request into the six columns the table shows: CCR No., Time,
// Date, Created By, Attendees and Decisions Made.
//
// `occurredAt` is the meeting's timestamp when the request came from a
// meeting, and the time it was raised otherwise, so the Date/Time columns are
// never blank.
// ─────────────────────────────────────────────
const toTableRow = (request) => {
  const when = request.occurredAt ? new Date(request.occurredAt) : null;
  const pad = (n) => String(n).padStart(2, "0");

  return {
    _id: request._id,
    // Already zero-padded by the server ("001"), so every view agrees.
    ccrNo: request.ccrNo || String(request.ccrNumber ?? 0).padStart(3, "0"),
    ccrNumber: request.ccrNumber ?? 0,
    time: when ? `${pad(when.getHours())}:${pad(when.getMinutes())}` : "—",
    date: when
      ? when.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—",
    createdBy: request.createdByName || "Unknown",
    attendeeNames: request.attendeeNames || [],
    decisionCount: request.decisionCount || 0,
    title: request.title || "",
    isFromMeeting: Boolean(request.isFromMeeting),
  };
};

// ─────────────────────────────────────────────
// SERVER → DETAIL CARD
//
// The card that opens under a clicked row. Reuses toTableRow for the shared
// fields so the card's header can never disagree with the row above it, then
// adds what only the card shows: meeting type, task, agenda notes, decisions,
// the changes raised from that meeting, and any attachments.
// ─────────────────────────────────────────────
const toDetailCard = (request) => {
  const meeting = request.meeting || null;

  return {
    ...toTableRow(request),
    title: request.title || "",
    meetingTitle: meeting?.title || "",
    meetingType: meeting?.meetingType || "—",
    taskName: meeting?.taskName || "—",
    notes: meeting?.notes || "",
    decisions: meeting?.decisions || [],
    attachments: (meeting?.attachments || []).map((file) => ({
      _id: file._id,
      name: file.name || "Attachment",
      url: file.url,
      // Drives whether the card shows a thumbnail or a file chip.
      isImage: file.fileType === "image",
      size: file.size || 0,
    })),
    raisedChanges: request.raisedChanges || [],
  };
};

// ─────────────────────────────────────────────
// LIST REQUESTS FOR A PROJECT
// ─────────────────────────────────────────────
const getChangeRequests = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/change-requests`);
  return response.data;
};

// ─────────────────────────────────────────────
// ONE REQUEST IN FULL
// ─────────────────────────────────────────────
const getChangeRequestById = async (projectId, requestId) => {
  const response = await api.get(
    `/projects/${projectId}/change-requests/${requestId}`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// RAISE A REQUEST
//
// Pass `sourceMinuteId` to carry the meeting's date, time, attendees and
// decision count onto the row; omit it for a standalone "New Request".
// ─────────────────────────────────────────────
const createChangeRequest = async (projectId, payload) => {
  const response = await api.post(
    `/projects/${projectId}/change-requests`,
    payload,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// APPROVE OR REJECT A CLIENT'S REQUEST
//
// Admin and manager only. Approving is when the CCR number is allocated and
// the row joins the table; rejecting keeps the record so the client can see
// what happened to it.
// ─────────────────────────────────────────────
const reviewChangeRequest = async (projectId, requestId, decision, note) => {
  const response = await api.patch(
    `/projects/${projectId}/change-requests/${requestId}/approval`,
    { decision, note },
  );
  return response.data;
};

// ─────────────────────────────────────────────
// UPDATE / DELETE
// ─────────────────────────────────────────────
const updateChangeRequest = async (projectId, requestId, payload) => {
  const response = await api.put(
    `/projects/${projectId}/change-requests/${requestId}`,
    payload,
  );
  return response.data;
};

const deleteChangeRequest = async (projectId, requestId) => {
  const response = await api.delete(
    `/projects/${projectId}/change-requests/${requestId}`,
  );
  return response.data;
};

const changeRequestService = {
  getChangeRequests,
  getChangeRequestById,
  createChangeRequest,
  reviewChangeRequest,
  updateChangeRequest,
  deleteChangeRequest,
  toTableRow,
  toDetailCard,
};

export default changeRequestService;
